import type { SEO, Website } from '@ps/web-martech-cms-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import URL from 'url-parse';

import { select } from '@swf/core/store';

function localizeCanonicalLink(link: string | undefined, locale: string) {
  return link ? `${locale}${link}` : link;
}

function normalizePathname(pathname: string) {
  if (pathname.endsWith('//')) {
    return pathname.replace(/\/+$/, '/');
  }
  return pathname;
}

const CMSSEO: React.FC<SEO> = ({
  meta: { title, canonical, description, keywords, robots: { noindex, nofollow } = {} }
}) => {
  const { meta_tags: metaTags } = useSelector(select.cms.getWebsite) as Website;
  const origin = useSelector(select.location.getOrigin);
  const pathname = useSelector(select.location.getPathname);
  const localeUrl = useSelector(select.location.getLocaleURL);
  const canonicalLink = useSelector(select.cms.getCanonicalLink);
  const pageAlternateLinks = useSelector(select.cms.getPageAlternateLinks);
  const websiteAlternateLinks = useSelector(select.cms.getAlternateLinks);
  const alternateLinks = pageAlternateLinks || websiteAlternateLinks;
  const canonicalWithLocale = localizeCanonicalLink(canonical, localeUrl);
  const canonicalLinkWithLocale = localizeCanonicalLink(canonicalLink, localeUrl);
  const canonicalHref = new URL(origin)
    .set('pathname', canonicalWithLocale || canonicalLinkWithLocale || normalizePathname(pathname))
    .toString();

  return title || description || keywords || noindex || canonicalLink || alternateLinks ? (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content={`noindex,${nofollow ? 'nofollow' : 'follow'}`} />}
      {metaTags?.map(({ name, content }) => (
        <meta key={content} name={name} content={content} />
      ))}
      {alternateLinks?.map(({ localeCode, url }) => (
        <link key={localeCode} rel="alternate" hrefLang={localeCode} href={url} />
      ))}
      <link rel="canonical" href={canonicalHref} />
    </Helmet>
  ) : null;
};
export default CMSSEO;
